// 1. Remove ToolCall from this import
"use client"
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { useEffect, useRef, useState } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../context/LiveAPIContext";
// 2. Import ToolCall and ToolResponse from the project's custom types
import { ToolCall, Tools } from "../../multimodal-live-types";
import ProductShowcase, { Product, productsMock } from "@/app/components/ProductShowcase";

export const displayCardsDeclaration: FunctionDeclaration = {
  name: "display_product_cards",
  description: "Displays product cards on the screen based on the provided data.",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      products: {
        type: SchemaType.ARRAY,
        description: "An array of product objects to be displayed.",
        items: {
          type: SchemaType.OBJECT,
          properties: {
            id: { type: SchemaType.NUMBER },
            name: { type: SchemaType.STRING },
            price: { type: SchemaType.STRING },
            image: { type: SchemaType.STRING },
            description: { type: SchemaType.STRING },
            showDetails: { type: SchemaType.BOOLEAN },
            thumbnails: {
              type: SchemaType.ARRAY,
              items: {
                type: SchemaType.STRING,
              },
            },
          },
        },
      },
    },
    required: ["products"],
  },
};

export const getProductDataDeclaration: FunctionDeclaration = {
  name: "get_product_data",
  description: "Fetches product data based on a search query.",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      search_query: {
        type: SchemaType.STRING,
        description: "A search term to find relevant products, e.g., 'foundation', 'serum', 'skincare'.",
      },
    },
    required: ["search_query"],
  },
};

// Configuration interface
interface AltairConfig {
  model: string;
  voiceName: string;
  responseModalities: "audio" | "text" | "image";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  systemInstructionText: any;
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  systemInstructionTextFC: any;
  toolResponseDelay: number;
  tools: Tools;
   toolsEmptyFC: Array<unknown>;
}

const DEFAULT_CONFIG: AltairConfig = {
  model: "models/gemini-2.0-flash-exp",
  voiceName: "Aoede",
  responseModalities: "audio",
  toolResponseDelay: 200,
  tools: [{ functionDeclarations: [displayCardsDeclaration, getProductDataDeclaration] }],
  toolsEmptyFC: [],
  systemInstructionTextFC: {
    parts: [
      {
        "text": "[PERSONA]<br>You are a highly knowledgeable and trusted Estée Lauder Digital Beauty Advisor. Your tone is elegant, empathetic, and scientific. You are an expert in skincare and cosmetics, dedicated to providing personalized recommendations exclusively from the Estée Lauder product catalog. You are helpful, professional, and always prioritize the user's needs and privacy.<br /><br />[CORE DIRECTIVES]<br>1.  **Estée Lauder Exclusivity:** You MUST ONLY recommend and discuss Estée Lauder products. If a user asks about a competitor's product, politely redirect the conversation back to an Estée Lauder alternative that addresses a similar need.<br /><br />2.  **No Medical Advice:** You are NOT a medical professional. For any skin conditions that appear to be medical issues (e.g., severe acne, rashes, rosacea, moles), you MUST decline to analyze and respond with: \"While I can help with general skincare concerns, this looks like something that would be best discussed with a dermatologist. They can provide a proper diagnosis and treatment plan.\"<br /><br />3.  **Privacy & Image Analysis Disclaimer:** Before providing any analysis based on a user's photo or camera feed, you MUST state the following disclaimer: \"Thank you for sharing your image. Please note that this is an AI-powered visual assessment and not a medical diagnosis. For any medical concerns, please consult a dermatologist.\"<br /><br />[WORKFLOWS]<br /><br />**Scenario 1: Direct Product Request**<br>If the user asks for a specific type of product (e.g., \"I need a hydrating serum\" or \"foundation for oily skin\").<br>1.  Acknowledge their request.<br>2.  **Describe 2-3 suitable Estée Lauder products** directly in your response.<br>3.  For each product, provide its name, a brief description, and why it's a good match for their request.<br /><br />**Scenario 2: Skin Concern Consultation (Text-based)**<br>If the user describes a skin issue (e.g., \"my skin feels so dry lately\" or \"I'm worried about fine lines\").<br>1.  Respond with empathy (e.g., \"I understand, dealing with dry skin can be frustrating.\").<br>2.  If necessary, ask one clarifying question to help narrow down the product choice (e.g., \"Could you tell me if your skin is also sensitive?\").<br>3.  Based on the concern, **recommend 2-3 specific Estée Lauder products** in your response, explaining how each one helps address the issue.<br /><br />**Scenario 3: Skin Analysis (Camera or Image Upload)**<br>If the user provides an image for analysis.<br>1.  **FIRST, state the mandatory disclaimer** (see Core Directive #3).<br>2.  Analyze the image to identify visible, general skin attributes. Focus on common concerns like: uneven skin tone, visible pores, redness, dryness/flakiness, or signs of aging (fine lines).<br>3.  Describe what you see in a positive and helpful way (e.g., \"I can see some areas of redness on the cheeks, which is quite common.\").<br>4.  Translate the visual observations into a product need. For example, 'redness' translates to a need for 'soothing' or 'calming' products.<br>5.  **Recommend 1-2 highly targeted Estée Lauder products** based on your analysis, clearly explaining the connection between your visual observation and your product recommendation."
      }
    ],
  },
  systemInstructionText: {
    parts: [
      {
        text: `You are a helpful product assistant. When a user asks for products:

1. FIRST: Use the "get_product_data" function with a search query to find relevant products
2. SECOND: IMMEDIATELY after receiving product data, use the "display_product_cards" function to show those exact products to the user

IMPORTANT: Always call display_product_cards after get_product_data to show the results to the user. Never skip this step.

Example flow:
- User asks for "foundation"
- Call get_product_data with search_query: "foundation"
- Receive product data
- IMMEDIATELY call display_product_cards with the received products`,
      }
    ],
  },
};

export function Altair() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [jsonString, setJSONString] = useState<string>("");
  const { client, setConfig } = useLiveAPIContext();
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    setConfig({
      model: DEFAULT_CONFIG.model,
      generationConfig: {
        responseModalities: DEFAULT_CONFIG.responseModalities,
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: DEFAULT_CONFIG.voiceName } },
        },
      },
      systemInstruction: DEFAULT_CONFIG.systemInstructionTextFC,
      tools: DEFAULT_CONFIG.toolsEmptyFC,
    });
  }, [setConfig]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      console.log(`got toolcall`, toolCall);
      
      // Handle display_product_cards
      const displayCardsCall = toolCall.functionCalls.find(
        (fc) => fc.name === "display_product_cards",
      );
      if (displayCardsCall) {
        const args = displayCardsCall.args as { products: Product[] };
        console.log("display_product_cards HERERERRERERERE", args.products);
        setProducts(args.products);
      }

      // Handle get_product_data
      const getDataCall = toolCall.functionCalls.find(
        (fc) => fc.name === "get_product_data",
      );
      if (getDataCall) {
        const { search_query } = getDataCall.args as { search_query: string };
        console.log(`Simulating API call for query: "${search_query}"`);
        
        const query = search_query.toLowerCase();
        const foundProducts = productsMock.filter(p => 
          p.name.toLowerCase().includes(query) || 
          p.description.toLowerCase().includes(query)
        );
        console.log("foundProducts", foundProducts);
      }

      // Send response back to the model (matching working version format)
      if (toolCall.functionCalls.length) {
        setTimeout(
          () => {
            // Handle get_product_data response
            const responses = toolCall.functionCalls.map((fc) => {
              if (fc.name === "get_product_data") {
                const { search_query } = fc.args as { search_query: string };
                const query = search_query.toLowerCase();
                const foundProducts = productsMock.filter(p => 
                  p.name.toLowerCase().includes(query) || 
                  p.description.toLowerCase().includes(query)
                );

                const mockApiData = foundProducts.length > 0 ? {
                  products: foundProducts,
                } : {
                  error: "No products found matching",
                  products: [],
                };

                console.log("Sending product data response:", mockApiData);
                
                return {
                  response: { output: mockApiData },
                  id: fc.id,
                };
              } else if (fc.name === "display_product_cards") {
                return {
                  response: { output: { success: true } },
                  id: fc.id,
                };
              }
              
              return {
                response: { output: { success: true } },
                id: fc.id,
              };
            });

            client.sendToolResponse({
              functionResponses: responses,
            });
          },
          200,
        );
      }
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString));
    }
  }, [embedRef, jsonString]);

  return <div className="vega-embed" ref={embedRef}>
    <ProductShowcase products={products} />
  </div>;
}